import { ActionIcon, Box, Group, Text, Tooltip } from "@mantine/core";
import {
  IconHome,
  IconListCheck,
  IconPlus,
  IconSchema,
  IconUser,
} from "@tabler/icons-react";
import { createElement } from "react";
import { useAuthLayout } from "../context";
import { useMobileActionButton } from "../hooks/useMobileActionButton";
import { useResolvedPathname } from "../hooks/useResolvedPathname";
import { getLegalLinks } from "../config/navigation";
import classes from "./Footer.module.css";
import { NavLink } from "@/components/navigation";

// Core navigation items for mobile footer (Projects/Rewards handled elsewhere)
const mobileNavItems = [
  {
    icon: createElement(IconHome, { size: 28 }),
    link: "/dashboard" as const,
    label: "Dashboard",
  },
  {
    icon: createElement(IconListCheck, { size: 28 }),
    link: "/tasks" as const,
    label: "Tasks",
  },
  {
    icon: createElement(IconSchema, { size: 28 }),
    link: "/assets" as const,
    label: "Assets",
  },
];

export default function Footer() {
  const { isAuthenticated } = useAuthLayout();
  const resolvedPathname = useResolvedPathname();
  const { actionButton, handleActionClick } = useMobileActionButton(
    resolvedPathname
  );
  const legalLinks = getLegalLinks();


  // Mobile authenticated footer
  if (isAuthenticated) {
    return (
      <Box className={classes.mobileFooter}>
        {/* Navigation tray */}
        <Box className={classes.navTray}>
          {mobileNavItems.map(({ icon, link, label }) => (
            <Tooltip key={label} label={label} position="top">
              <NavLink
                to={link}
                className={classes.navLink}
                aria-label={label}
                underline="never"
              >
                {icon}
              </NavLink>
            </Tooltip>
          ))}

          {/* Profile settings - NavLink for router preload and consistency */}
          <Tooltip label="Settings" position="top">
            <NavLink
              to="/settings"
              className={classes.navLink}
              aria-label="Settings"
              underline="never"
            >
              <IconUser size={28} />
            </NavLink>
          </Tooltip>
        </Box>

        {/* FAB - context-aware action (+), fixed on the right */}
        {actionButton && (
          <ActionIcon
            type="button"
            variant="filled"
            size="md"
            radius="md"
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleActionClick();
            }}
            className={classes.fab}
            aria-label={actionButton.label}
          >
            <IconPlus size={20} />
          </ActionIcon>
        )}
      </Box>
    );
  }

  // Non-authenticated footer (legal links)
  return (
    <Box className={classes.footer} py="xs" px="md">
      <Group justify="space-between" w="100%" wrap="wrap" gap="xs">
        <Group gap="xs" wrap="nowrap" className={classes.links}>
          {legalLinks.map(({ label, link }) => (
            <NavLink
              key={label}
              to={link}
              c="gray.0"
              underline="never"
              size="xs"
            >
              {label}
            </NavLink>
          ))}
        </Group>
        <Group gap="xs" wrap="nowrap">
          <Text size="xs" c="gray.0">
            &copy; 2026 Turtle-O Tech, Ltd.
          </Text>
          <Text size="xs" c="gray.0">All rights reserved.</Text>
        </Group>
      </Group>
    </Box>
  );
}
